// Detalle del equipo en «Elige el teléfono»: ficha con precio, enganche y financieras, y las promos por tipo.
// Se pinta desde la selección local, así sigue visible aunque el equipo apartado salga del catálogo.
import type { ReactNode } from 'react';
import { Smartphone } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { financiersFor } from '../cardBenefits';
import { creditPromotionRows, type CreditPromotionRow } from '../creditPromotions';
import type { RenderAccountBonuses } from '../promotionSlots';
import type { CatalogItem, PlanLevel, ProductCommercialInfo, ReservationState } from '../types';
import { CreditPromotionsGroup } from './CreditPromotionsGroup';
import { FinancierBadge } from './FinancierBadge';
import { StockChip, ToneChip } from './StockChip';
import { variantLabel } from '@/shared/lib/format/capacity';

export function ProductDetailCard({
    picked,
    stock,
    service = false,
    reservation,
    imei,
    clientLevel,
    salePromotions,
    discountedPriceCents = null,
    downPayment = null,
    creditPromotions,
    creditNote,
    kitComponents,
    onShowKitContents,
    hideCreditPromotionLabel = false,
}: {
    /** Con `financeable`/`affordableOnCredit` del catálogo, «Financian» dice lo mismo que la tarjeta. */
    picked:
        | (ProductCommercialInfo & Partial<Pick<CatalogItem, 'financeable' | 'affordableOnCredit'>>)
        | null;
    /** Stock vivo del SKU. `null` = ya no está en el catálogo (típicamente porque lo apartaste tú). */
    stock: number | null;
    /** Un servicio no lleva inventario: el panel no habla de stock. */
    service?: boolean;
    /** Estado de la reserva del SKU: `held` retiene una unidad, `expired` la perdió a los 30 min. */
    reservation: ReservationState;
    /** IMEI de la unidad apartada — el número de la caja que el vendedor tiene que bajar del anaquel. */
    imei: string | null;
    /** Nivel del cliente (score adelantado). Sin él, el panel muestra todos los grupos como condicionales. */
    clientLevel?: PlanLevel | null;
    /** Bloques de Promociones; pinta «Bonos a tu cuenta» con `renderBonuses` para juntar los de crédito. */
    salePromotions?: (renderBonuses: RenderAccountBonuses) => ReactNode;
    /** Precio con la promo de venta; `null` si ninguna lo baja. */
    discountedPriceCents?: number | null;
    /** Enganche desde con SureKeep y de dónde sale; `null` si no se sabe o no lo financia. */
    downPayment?: { cents: number; note: string | null } | null;
    /** Promos de crédito de este equipo; sin ellas se leen las del catálogo del equipo. */
    creditPromotions?: readonly CreditPromotionRow[];
    /** Sin nivel resuelto se muestran «según su nivel»; si no califica, el grupo lo dice. */
    creditNote?: 'levelPending' | 'notEligible' | undefined;
    /** Componentes del kit elegido; con el handler muestra «Ver qué incluye». */
    kitComponents?: number;
    onShowKitContents?: () => void;
    /** Oculta el header CRÉDITO del grupo de promos de crédito; el color morado basta. */
    hideCreditPromotionLabel?: boolean;
}) {
    const apartado = reservation === 'held';
    const financiers = picked ? financiersFor(picked) : [];
    return (
        <aside className="rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
                <div>
                    <div className="text-[13px] font-semibold">Detalle del equipo</div>
                </div>
            </div>

            <div className="p-4">
                {picked ? (
                    <>
                        <div className="mb-3 flex items-start gap-3">
                            <Smartphone
                                className="size-10 flex-none text-muted-foreground"
                                aria-hidden
                            />
                            <div className="min-w-0 flex-1">
                                <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                                    {picked.brand}
                                </div>
                                <div className="text-[15px] font-semibold leading-tight">
                                    {picked.model !== '' ? picked.model : itemDisplayName(picked)}
                                </div>
                                {onShowKitContents !== undefined && kitComponents !== undefined && (
                                    <button
                                        type="button"
                                        onClick={onShowKitContents}
                                        className="mt-1 rounded-sm text-[12px] font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    >
                                        Ver qué incluye ({kitComponents})
                                    </button>
                                )}
                                {apartado ? (
                                    <div className="mt-1">
                                        <ToneChip tone="success">Apartado</ToneChip>
                                    </div>
                                ) : reservation === 'expired' ? (
                                    <div className="mt-1">
                                        <ToneChip tone="warning">La reserva venció</ToneChip>
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        <div className="my-3 h-px bg-border" />

                        <dl className="grid grid-cols-3 gap-2 text-[12px]">
                            <dt className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                                SKU
                            </dt>
                            <dd className="col-span-2 font-mono">{picked.sku}</dd>

                            <dt className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                                Precio
                            </dt>
                            <dd className="col-span-2 flex flex-wrap items-baseline gap-x-2 tabular-nums">
                                {discountedPriceCents !== null &&
                                    discountedPriceCents < picked.cashPriceCents && (
                                        <span className="text-muted-foreground line-through">
                                            {formatMXN(picked.cashPriceCents / 100)}
                                        </span>
                                    )}
                                <span
                                    className={cn(
                                        'text-[15px] font-semibold',
                                        discountedPriceCents !== null &&
                                            discountedPriceCents < picked.cashPriceCents &&
                                            'text-success',
                                    )}
                                >
                                    {formatMXN(
                                        (discountedPriceCents !== null &&
                                        discountedPriceCents < picked.cashPriceCents
                                            ? discountedPriceCents
                                            : picked.cashPriceCents) / 100,
                                    )}
                                </span>
                            </dd>

                            {downPayment !== null && (
                                <>
                                    <dt className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                                        Enganche
                                    </dt>
                                    <dd className="col-span-2">
                                        <span className="tabular-nums">
                                            desde{' '}
                                            <strong className="font-semibold">
                                                {formatMXN(downPayment.cents / 100)}
                                            </strong>
                                        </span>
                                        {downPayment.note && (
                                            <span className="block text-[11px] text-muted-foreground">
                                                {downPayment.note}
                                            </span>
                                        )}
                                    </dd>
                                </>
                            )}

                            <dt className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                                Financian
                            </dt>
                            <dd className="col-span-2 flex flex-wrap gap-1">
                                {financiers.length > 0
                                    ? financiers.map((code) => (
                                          <FinancierBadge key={code} code={code} />
                                      ))
                                    : 'Solo de contado'}
                            </dd>

                            <dt className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                                Equipo
                            </dt>
                            <dd className="col-span-2">{variantLabel(picked) || '—'}</dd>

                            <dt className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                                Existencia
                            </dt>
                            <dd className="col-span-2">
                                {service ? (
                                    <StockChip item={{ itemType: 'SERVICE', stock: null }} />
                                ) : stock !== null ? (
                                    <StockChip item={{ stock }} />
                                ) : (
                                    <ToneChip tone="warning">Ya no está en el catálogo</ToneChip>
                                )}
                            </dd>

                            {imei !== null && (
                                <>
                                    <dt className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                                        IMEI
                                    </dt>
                                    <dd className="col-span-2 font-mono text-[11.5px]">{imei}</dd>
                                </>
                            )}
                        </dl>

                        <div key={picked.sku}>
                            {salePromotions?.(() => null)}
                            <CreditPromotionsGroup
                                rows={
                                    creditPromotions ??
                                    (financiers.length === 0
                                        ? []
                                        : creditPromotionRows([], [picked], clientLevel))
                                }
                                clientLevel={clientLevel}
                                note={creditNote}
                                hideLabel={hideCreditPromotionLabel}
                            />
                        </div>
                    </>
                ) : (
                    <div className="py-6 text-center text-[12.5px] text-muted-foreground">
                        Toca un equipo para ver sus promos.
                    </div>
                )}
            </div>
        </aside>
    );
}
